import React, { useState, useEffect } from 'react';
import './Settings.css';

function Settings({ onClose }) {
  const [audioInputs, setAudioInputs] = useState([]);
  const [audioOutputs, setAudioOutputs] = useState([]);
  const [videoInputs, setVideoInputs] = useState([]);
  
  const [selectedAudioInput, setSelectedAudioInput] = useState('');
  const [selectedAudioOutput, setSelectedAudioOutput] = useState('');
  const [selectedVideoInput, setSelectedVideoInput] = useState('');
  
  const [inputGain, setInputGain] = useState(100);
  const [outputGain, setOutputGain] = useState(100);
  
  const [noiseSuppression, setNoiseSuppression] = useState(true);
  const [echoCancellation, setEchoCancellation] = useState(true);

  useEffect(() => {
    // Load settings from localStorage
    const settings = JSON.parse(localStorage.getItem('audioSettings') || '{}');
    setSelectedAudioInput(settings.audioInput || '');
    setSelectedAudioOutput(settings.audioOutput || '');
    setSelectedVideoInput(settings.videoInput || '');
    setInputGain(settings.inputGain || 100);
    setOutputGain(settings.outputGain || 100);
    setNoiseSuppression(settings.noiseSuppression !== false);
    setEchoCancellation(settings.echoCancellation !== false);

    // Get available devices
    getDevices();

    // Listen for device changes
    navigator.mediaDevices.addEventListener('devicechange', getDevices);
    return () => {
      navigator.mediaDevices.removeEventListener('devicechange', getDevices);
    };
  }, []);

  const getDevices = async () => {
    try {
      // Request permission first
      await navigator.mediaDevices.getUserMedia({ audio: true, video: true });
      
      const devices = await navigator.mediaDevices.enumerateDevices();
      
      const inputs = devices.filter(d => d.kind === 'audioinput');
      const outputs = devices.filter(d => d.kind === 'audiooutput');
      const videos = devices.filter(d => d.kind === 'videoinput');
      
      setAudioInputs(inputs);
      setAudioOutputs(outputs);
      setVideoInputs(videos);
    } catch (error) {
      console.error('Error getting devices:', error);
    }
  };

  const handleSave = () => {
    const settings = {
      audioInput: selectedAudioInput,
      audioOutput: selectedAudioOutput,
      videoInput: selectedVideoInput,
      inputGain,
      outputGain,
      noiseSuppression,
      echoCancellation
    };
    
    localStorage.setItem('audioSettings', JSON.stringify(settings));
    
    // Dispatch custom event so VoiceChannel can update its settings
    window.dispatchEvent(new CustomEvent('audioSettingsChanged', { detail: settings }));
    
    onClose();
  };

  return (
    <div className="settings-backdrop">
      <div className="settings-modal">
        <div className="settings-header">
          <h2>Voice & Video Settings</h2>
          <button className="settings-close" onClick={onClose}>×</button>
        </div>

        <div className="settings-content">
          {/* Audio Input */}
          <div className="settings-section">
            <h3>Input Device</h3>
            <select 
              value={selectedAudioInput} 
              onChange={(e) => setSelectedAudioInput(e.target.value)}
              className="device-select"
            >
              <option value="">Default</option>
              {audioInputs.map(device => (
                <option key={device.deviceId} value={device.deviceId}>
                  {device.label || `Microphone ${device.deviceId.slice(0, 8)}`}
                </option>
              ))}
            </select>
          </div>

          {/* Audio Output */}
          <div className="settings-section">
            <h3>Output Device</h3>
            <select 
              value={selectedAudioOutput} 
              onChange={(e) => setSelectedAudioOutput(e.target.value)}
              className="device-select"
            >
              <option value="">Default</option>
              {audioOutputs.map(device => (
                <option key={device.deviceId} value={device.deviceId}>
                  {device.label || `Speaker ${device.deviceId.slice(0, 8)}`}
                </option>
              ))}
            </select>
          </div>

          {/* Video Input */}
          <div className="settings-section">
            <h3>Camera</h3>
            <select 
              value={selectedVideoInput} 
              onChange={(e) => setSelectedVideoInput(e.target.value)}
              className="device-select"
            >
              <option value="">Default</option>
              {videoInputs.map(device => (
                <option key={device.deviceId} value={device.deviceId}>
                  {device.label || `Camera ${device.deviceId.slice(0, 8)}`}
                </option>
              ))}
            </select>
          </div>

          {/* Input Gain */}
          <div className="settings-section">
            <h3>Input Volume</h3>
            <div className="gain-control">
              <input
                type="range"
                min="0"
                max="200"
                value={inputGain}
                onChange={(e) => setInputGain(parseInt(e.target.value))}
                className="gain-slider"
              />
              <span className="gain-value">{inputGain}%</span>
            </div>
          </div>

          {/* Output Gain */}
          <div className="settings-section">
            <h3>Output Volume</h3>
            <div className="gain-control">
              <input
                type="range"
                min="0"
                max="200"
                value={outputGain}
                onChange={(e) => setOutputGain(parseInt(e.target.value))}
                className="gain-slider"
              />
              <span className="gain-value">{outputGain}%</span>
            </div>
          </div>

          {/* Noise Suppression */}
          <div className="settings-section">
            <div className="toggle-setting">
              <div>
                <h3>Noise Suppression</h3>
                <p className="setting-description">Reduce background noise from your microphone</p>
              </div>
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={noiseSuppression}
                  onChange={(e) => setNoiseSuppression(e.target.checked)}
                />
                <span className="toggle-slider"></span>
              </label>
            </div>
          </div>

          {/* Echo Cancellation */}
          <div className="settings-section">
            <div className="toggle-setting">
              <div>
                <h3>Echo Cancellation</h3>
                <p className="setting-description">Prevent audio feedback and echo</p>
              </div>
              <label className="toggle-switch">
                <input
                  type="checkbox"
                  checked={echoCancellation}
                  onChange={(e) => setEchoCancellation(e.target.checked)}
                />
                <span className="toggle-slider"></span>
              </label>
            </div>
          </div>
        </div>

        <div className="settings-footer">
          <button className="btn-cancel" onClick={onClose}>Cancel</button>
          <button className="btn-save" onClick={handleSave}>Save Changes</button>
        </div>
      </div>
    </div>
  );
}

export default Settings;
